"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { useTranslation } from "react-i18next";
import { getLocaleFromPath, getPathnameWithoutLocale } from "../lib/i18n/utils";
import Icon from "./Icon";
import LanguageSwitcher from "./LanguageSwitcher";
import BookingCTA from "./BookingCTA";

export default function Header() {
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const { t } = useTranslation();
  const locale = getLocaleFromPath(pathname);
  const pathWithoutLocale = getPathnameWithoutLocale(pathname);

  // Greek (el) is the default locale and MUST NOT appear in URLs
  // English (en) MUST be prefixed with /en
  const navLinks = [
    { href: locale === "el" ? "/" : `/${locale}`, label: t("The guesthouse") },
    { href: locale === "el" ? "/where" : `/${locale}/where`, label: t("Where we are") },
    { href: locale === "el" ? "/rooms" : `/${locale}/rooms`, label: t("Rooms") },
    { href: locale === "el" ? "/area" : `/${locale}/area`, label: t("The area") },
    { href: locale === "el" ? "/contact" : `/${locale}/contact`, label: t("Contact") },
  ];

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const isActive = (href: string) => {
    // Normalize href by removing trailing slash (except for root)
    const normalizedHref = href !== '/' && href.endsWith('/') 
      ? href.slice(0, -1) 
      : href;
    
    // Determine homepage href based on locale
    const homeHref = locale === "el" ? "/" : `/${locale}`;
    const isHomepageLink = normalizedHref === homeHref;
    
    if (isHomepageLink) {
      // Homepage: Only active on exact match - no sub-route matching
      // For el: pathname must be exactly "/"
      // For en: pathname must be exactly "/en"
      return pathname === homeHref;
    }
    
    // Other nav links: Active on exact match OR when current path is a sub-route
    // Example: "/en/rooms" is active on "/en/rooms" and "/en/rooms/budget"
    // But NOT active on "/en/room" (different word)
    if (pathname === normalizedHref) {
      return true;
    }
    
    // Check if pathname is a sub-route of this nav link
    // Must start with href + '/' to ensure proper word boundary
    return pathname.startsWith(`${normalizedHref}/`);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-200 bg-ui-page">
      <div className="relative h-17.5 w-full lg:hidden">
        <Link
          href={locale === "el" ? "/" : `/${locale}`}
          className="absolute left-2.5 top-2.5"
          onClick={() => setOpen(false)}
        >
          <Image
            src="/logo-full.png"
            alt={t("To Spiti Tis Sofias")}
            width={312}
            height={217}
            className="w-19.5 h-auto"
            quality={100}
            priority
          />
        </Link>

        <div className="absolute top-3.75 right-16.75" onClick={() => setOpen(false)}>
          <BookingCTA variant="mobile" />
        </div>

        <button
          type="button"
          className="absolute right-2.5 top-5 h-7.5 w-6.75"
          aria-label={open ? t("Close menu") : t("Open menu")}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span className="relative block h-full w-full">
            <span
              className={`absolute left-0 h-0.5 w-full bg-brand-text transition-all duration-300 ease-in-out ${
                open
                  ? "top-1/2 -translate-y-1/2 rotate-45"
                  : "top-0 translate-y-0 rotate-0"
              }`}
            />
            <span
              className={`absolute left-0 top-1/2 h-0.5 w-full -translate-y-1/2 bg-brand-text transition-all duration-300 ease-in-out ${
                open ? "opacity-0 scale-0" : "opacity-100 scale-100"
              }`}
            />
            <span
              className={`absolute left-0 h-0.5 w-full bg-brand-text transition-all duration-300 ease-in-out ${
                open
                  ? "top-1/2 -translate-y-1/2 -rotate-45"
                  : "bottom-0 translate-y-0 rotate-0"
              }`}
            />
          </span>
        </button>
      </div>

      {open && (
        <>
          <button
            className="fixed left-0 right-0 bottom-0 top-17.5 z-40 bg-black/20 lg:hidden"
            style={{
              animation: 'fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1) both'
            }}
            aria-label={t("Close menu")}
            onClick={() => setOpen(false)}
          />

          <div 
            className="fixed right-0 top-17.5 z-50 h-[calc(100vh-4.375rem)] w-54.25 bg-[#FBFBFB] lg:hidden"
            style={{
              animation: 'slideInMenu 0.4s cubic-bezier(0.16, 1, 0.3, 1) both'
            }}
          >
            <nav className="flex flex-col items-start gap-7.5 pl-5 pt-8 text-brand-text">
              {navLinks.map((l, index) => (
                <Link
                  key={l.href}
                  href={l.href}
                  className={`nav-link w-fit ${
                    isActive(l.href) ? "is-active" : ""
                  }`}
                  style={{
                    animation: `slideInLeft 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${0.2 + index * 0.08}s both`,
                  }}
                  data-text={l.label}
                  onClick={() => setOpen(false)}
                  suppressHydrationWarning
                >
                  <span suppressHydrationWarning>{l.label}</span>
                </Link>
              ))}
              <div 
                className="pt-2 min-h-[32px] flex items-center"
                style={{
                  animation: `slideInLeft 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${0.2 + navLinks.length * 0.08}s both`,
                }}
              >
                <LanguageSwitcher />
              </div>
            </nav>
          </div>
        </>
      )}

      <div className="hidden lg:flex h-22.5 w-full items-center px-3 xl:px-2.5">
        <div className="mx-auto flex w-full items-center justify-between">
          <div className="flex items-center gap-10 xl:gap-25 shrink-0">
            <Link href={locale === "el" ? "/" : `/${locale}`} className="flex shrink-0 items-center">
              <Image
                src="/logo-full.png"
                alt={t("To Spiti Tis Sofias")}
                width={436}
                height={304}
                className="w-24 h-auto xl:w-27.25 xl:h-auto"
                quality={100}
                priority
              />
            </Link>

            <nav className="flex items-center gap-5 xl:gap-12.5 whitespace-nowrap text-[0.8125rem] xl:text-sm text-brand-text">
              {navLinks.map((l) => (
                <Link
                  key={l.href}
                  href={l.href}
                  className={`nav-link ${
                    isActive(l.href) ? "is-active" : ""
                  }`}
                  data-text={l.label}
                  suppressHydrationWarning
                >
                  <span suppressHydrationWarning>{l.label}</span>
                </Link>
              ))}
            </nav>
          </div>

          <div className="flex items-center shrink-0">
            <LanguageSwitcher />
          </div>

          <div className="flex items-center gap-3 xl:gap-5 shrink-0">
            <Link
              href="tel:+302144081916"
              className="group flex items-center gap-2 shrink-0 whitespace-nowrap text-[0.8125rem] xl:text-sm font-light text-ui-card transition hover:text-brand-accent"
            >
              <Icon name="phone" size="md" className="text-ui-card transition group-hover:text-brand-accent" />
              {t("(+30) 2144081916")}
            </Link>

            <BookingCTA
              className="shrink-0 h-13 px-5 text-[0.875rem] xl:h-15 xl:px-7 xl:text-base text-ui-card"
            />
          </div>
        </div>
      </div>
    </header>
  );
}
